import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';

import { apiClient } from '@shared/api';
import { categories, type CategoryId } from '@shared/config/categories';
import { notify } from '@shared/ui/notifications/notify';

export { categories, isCampaignCategory, isCategoryEnabled, type CategoryId } from '@shared/config/categories';

const keyFor = (login?: string) => `uznali:categories:${login ?? 'guest'}`;
const setupKeyFor = (login?: string) => `uznali:categories-setup:${login ?? 'guest'}`;
const defaultSelection: CategoryId[] = categories.map(({ id }) => id);
const apiKindByCategory: Record<CategoryId, string> = {
  movies: 'film',
  series: 'series',
  games: 'game',
  anime: 'anime',
};
const knownCategories = (values: string[]) =>
  defaultSelection.filter((id) => values.includes(apiKindByCategory[id]) || values.includes(id));

export const isCategorySetupComplete = (login?: string) => {
  try {
    return window.localStorage.getItem(setupKeyFor(login)) === '1';
  } catch {
    return false;
  }
};

export const completeCategorySetup = (login?: string) => {
  try {
    window.localStorage.setItem(setupKeyFor(login), '1');
  } catch {
    // The setup still completes for the current visit.
  }
};

export const readSavedCategorySelection = (login?: string): CategoryId[] | null => {
  if (login) return null;
  try {
    const saved = window.localStorage.getItem(keyFor());
    if (!saved) return null;
    const parsed: unknown = JSON.parse(saved);
    if (!Array.isArray(parsed)) return null;
    const selected = knownCategories(parsed.filter((value): value is string => typeof value === 'string'));
    return selected.length ? selected : null;
  } catch {
    return null;
  }
};

class CategoryPreferencesStore {
  selected: CategoryId[];
  loaded = false;
  loading = false;
  saving = false;
  error: string | null = null;
  private revision = 0;
  private request: Promise<void> | null = null;

  constructor(private readonly login?: string) {
    this.selected = login ? defaultSelection : (readSavedCategorySelection() ?? defaultSelection);
    this.loaded = !login;
    makeAutoObservable<this, 'request'>(this, { request: false }, { autoBind: true });
    if (!login && typeof window !== 'undefined') window.addEventListener('storage', this.onStorage);
  }

  private onStorage = (event: StorageEvent) => {
    if (event.storageArea === window.localStorage && event.key === keyFor()) {
      this.selected = readSavedCategorySelection() ?? defaultSelection;
    }
  };

  load(): Promise<void> {
    if (!this.login || this.loaded) return Promise.resolve();
    if (this.request) return this.request;
    this.request = this.loadPreferences();
    return this.request;
  }

  private async loadPreferences() {
    if (!this.login) return;
    this.loading = true;
    this.error = null;
    const revision = this.revision;
    try {
      const response = await apiClient.users.getCategories(this.login);
      runInAction(() => {
        if (this.revision === revision) {
          const selected = knownCategories(response.categories);
          this.selected = selected.length ? selected : defaultSelection;
          this.loaded = true;
        }
      });
    } catch {
      runInAction(() => {
        this.error = 'Не удалось загрузить категории';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.request = null;
      });
    }
  }

  async save(selected: CategoryId[]) {
    if (!selected.length || this.saving) return;
    this.revision += 1;
    const previous = this.selected;
    this.selected = selected;
    if (!this.login) {
      try {
        window.localStorage.setItem(keyFor(), JSON.stringify(selected));
      } catch {
        // The selection remains available for this visit.
      }
      return;
    }
    this.saving = true;
    try {
      const response = await apiClient.users.updateCategories({
        categories: selected.map((id) => apiKindByCategory[id]),
      });
      runInAction(() => {
        const saved = knownCategories(response.categories);
        this.selected = saved.length ? saved : selected;
        this.loaded = true;
      });
    } catch {
      runInAction(() => {
        this.selected = previous;
      });
      notify.error({ title: 'Не удалось сохранить категории', message: 'Попробуйте ещё раз.' });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  toggle = (id: CategoryId) => {
    const next = this.selected.includes(id) ? this.selected.filter((item) => item !== id) : [...this.selected, id];
    void this.save(next);
  };
}

const stores = new Map<string, CategoryPreferencesStore>();
const getStore = (login?: string) => {
  const key = login ?? 'guest';
  let store = stores.get(key);
  if (!store) {
    store = new CategoryPreferencesStore(login);
    stores.set(key, store);
  }
  return store;
};

export const readCategorySelection = (login?: string): CategoryId[] => getStore(login).selected;
export const loadCategoryPreferences = async (login?: string) => {
  const store = getStore(login);
  await store.load();
  if (store.error) throw new Error(store.error);
};

export const saveCategorySelection = (login: string | undefined, selected: CategoryId[]) =>
  getStore(login).save(selected);

export const useCategoryPreferences = (login?: string) => {
  const store = getStore(login);
  useEffect(() => {
    void store.load();
  }, [store]);
  return store;
};
